import { useState, useEffect, useCallback } from 'react';
import { useServices } from '@/app/providers';
import type { PatrolCheckpoint, CreateCheckpointDTO, UpdateCheckpointDTO, CheckpointQRResponse } from '@yawi/types';

export function usePatrolCheckpoints(activeOnly: boolean = true) {
  const { logbookRepo } = useServices();
  const [checkpoints, setCheckpoints] = useState<PatrolCheckpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCheckpoints = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await logbookRepo.getCheckpoints(activeOnly);
      // Sort by orderIndex
      setCheckpoints(result.sort((a, b) => a.orderIndex - b.orderIndex));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar puntos de control');
    } finally {
      setLoading(false);
    }
  }, [logbookRepo, activeOnly]);

  useEffect(() => {
    fetchCheckpoints();
  }, [fetchCheckpoints]);

  const createCheckpoint = useCallback(async (data: CreateCheckpointDTO) => {
    try {
      const newCheckpoint = await logbookRepo.createCheckpoint(data);
      setCheckpoints(prev => [...prev, newCheckpoint].sort((a, b) => a.orderIndex - b.orderIndex));
      return newCheckpoint;
    } catch (err) {
      throw err instanceof Error ? err : new Error('Error al crear punto de control');
    }
  }, [logbookRepo]);

  const updateCheckpoint = useCallback(async (id: string, data: UpdateCheckpointDTO) => {
    try {
      const updated = await logbookRepo.updateCheckpoint(id, data);
      setCheckpoints(prev =>
        prev.map(cp => cp.id === id ? updated : cp).sort((a, b) => a.orderIndex - b.orderIndex)
      );
      return updated;
    } catch (err) {
      throw err instanceof Error ? err : new Error('Error al actualizar punto de control');
    }
  }, [logbookRepo]);

  const getCheckpointQR = useCallback(async (id: string): Promise<CheckpointQRResponse> => {
    try {
      return await logbookRepo.getCheckpointQR(id);
    } catch (err) {
      throw err instanceof Error ? err : new Error('Error al obtener QR');
    }
  }, [logbookRepo]);

  const deleteCheckpoint = useCallback(async (id: string) => {
    try {
      await logbookRepo.deleteCheckpoint(id);
      setCheckpoints(prev => prev.filter(cp => cp.id !== id));
    } catch (err) {
      throw err instanceof Error ? err : new Error('Error al eliminar punto de control');
    }
  }, [logbookRepo]);

  return {
    checkpoints,
    loading,
    error,
    createCheckpoint,
    updateCheckpoint,
    deleteCheckpoint,
    getCheckpointQR,
    refresh: fetchCheckpoints,
  };
}
